// CAN THE VIEWER ACTUALLY OPEN THIS AGENT DM? The predicate behind the
// "unclearable red badge" class.
//
// THE BUG. A plain provider session — a Claude/Codex harness run with no cybo
// identity — can DM a human through `cyborg7_send_message { to }`. That is
// intended, and it deliberately mints NO session on the recipient's side
// (message-router.ts postAgentMessage: `if (cyboId) this.ensureDefaultSession(…)`,
// "a raw agent with no cybo identity has no default-session concept"). The DM is
// persisted, broadcast, and the recipient's client badges it under the sender's id
// (`fromId = cybo_id ?? agentId` → the raw agent uuid). What the recipient does NOT
// get is anywhere to READ it:
//   • Direct Messages is built from the CYBO ROSTER (session-scope.ts
//     `buildDmAgentRows(cyboState.list, …)`, whose session map ingests only rows
//     with a truthy `cyboId`) — a raw agent is structurally unrepresentable there;
//   • the "Sessions" list is `filterMine`-scoped, so ANOTHER user's session (or an
//     ephemeral one) is dropped;
//   • `/agent/<rawId>` resolves only while the row is still in the roster, and
//     bounces to the last channel when it isn't — its markDmRead effect early-
//     returns first, so even the push deep link cannot clear the badge.
// The badge is keyed on an id nothing renders: a red number with no door. It is
// not even self-limiting — `getDmUnreadCounts` re-counts the row on every
// `fetch_unread` for as long as the sender's `agent_sessions` row is unarchived,
// so a local clear is undone by the next reseed.
//
// So the honest question is not "is this sender a cybo" but "does THIS VIEWER have
// a surface they can open" — which is what this answers, in the same vocabulary
// the sidebar itself uses, so the two cannot drift. A `false` here is the caller's
// cue to put the DM in Activity (the app's inbox), where clicking it runs the
// normal `dmPeer` read scope: `client.markDmRead` → the shared `dm_reads` cursor.
//
// Pure + dependency-light so it is unit-testable under plain-node vitest — the
// same split cybo-dm-peer-id.ts was extracted for.
import {
  belongsInMineSidebar,
  isMe,
  isMine,
  type SessionScopeFields,
  type ViewerIdentity,
} from "./session-scope.js";
import { agentDisplayName, type AgentNameSource } from "./agent-display.js";

// A session row as this predicate reads it: its own id, its cybo binding, and the
// scope fields `belongsInMineSidebar` needs. A structural subset of the live agent
// row, so callers pass `workspaceState.agents` straight through.
export interface AgentDmSessionRow extends SessionScopeFields {
  agentId: string;
  cyboId?: string | null;
}

// Does the viewer have a DM surface they can reach for `peerId` (the canonical
// agent-DM peer id — a cybo id when the sender is cybo-bound, else the raw agent
// id)? True when EITHER:
//
//  1. the peer is a cybo in the workspace roster — Direct Messages walks that
//     roster and builds a row for every entry, session or not (session-scope.ts
//     `buildDmAgentRows`), so the conversation is always one click away; or
//  2. a session with that id belongs in the viewer's OWN sidebar
//     (`belongsInMineSidebar`) — the "Sessions" rows, plus the roster-less cybo
//     rows `buildDmAgentRows` appends after the roster ones. Deliberately the
//     sidebar predicate and not a bare "is it in workspaceState.agents": that
//     roster is the FULL workspace list, so an id present there can still be
//     another user's personal session or a one-turn ephemeral, neither of which
//     the viewer has any way to navigate to.
//
// Conservative in the direction that cannot lose a message: an unloaded roster
// (a cold workspace open, a daemon still answering the `list_agents` fan-out)
// reads as "no surface", which costs at worst one redundant Activity row for a
// DM the viewer really did receive — the same row a human DM always gets. The
// opposite default would drop the only signal there is.
export function agentDmHasReachableSurface(
  peerId: string,
  cyboIds: readonly string[],
  sessions: readonly AgentDmSessionRow[],
  viewer: ViewerIdentity,
): boolean {
  if (!peerId) return false;
  if (cyboIds.includes(peerId)) return true;
  const own = sessions.find((s) => s.agentId === peerId);
  return !!own && belongsInMineSidebar(own, viewer);
}

// Click-time twin of the above for an Activity alert: also true for the viewer's OWN session
// even when ephemeral (isMine: id or email), which the sidebar drops. Feeds the registry's
// `isAgentDmReachable` so both Activity surfaces route an agent DM to /agent/<id>.
export function isAgentDmReachableForViewer(
  peerId: string,
  cyboIds: readonly string[],
  sessions: readonly AgentDmSessionRow[],
  viewer: ViewerIdentity,
): boolean {
  const own = sessions.find((s) => s.agentId === peerId);
  return (
    (!!own && isMine(own, viewer)) || agentDmHasReachableSurface(peerId, cyboIds, sessions, viewer)
  );
}

// When a DM's peer has no sidebar row, the rosters are re-read once per peer per
// cooldown, plus one retry when that read fails — never a read per message.
export class DmSurfaceHealGate {
  private readonly lastHeal = new Map<string, number>();
  private readonly retrying = new Set<string>();

  constructor(private readonly cooldownMs: number) {}

  shouldHeal(workspaceId: string, peerId: string, now: number): boolean {
    const key = healKey(workspaceId, peerId);
    const at = this.lastHeal.get(key);
    if (at !== undefined && now - at < this.cooldownMs) return false;
    this.lastHeal.set(key, now);
    return true;
  }

  retryAfterFailure(workspaceId: string, peerId: string): boolean {
    const key = healKey(workspaceId, peerId);
    if (this.retrying.delete(key)) return false;
    this.retrying.add(key);
    this.lastHeal.delete(key);
    return true;
  }

  succeeded(workspaceId: string, peerId: string): void {
    this.retrying.delete(healKey(workspaceId, peerId));
  }

  reset(): void {
    this.lastHeal.clear();
    this.retrying.clear();
  }

  size(): number {
    return this.lastHeal.size + this.retrying.size;
  }
}

function healKey(workspaceId: string, peerId: string): string {
  return `${workspaceId}\u0000${peerId}`;
}

// What the heal pump reads of the app (#3629 C): which workspace is on screen, whether a
// roster read is already out, whether a peer has a surface, and the re-read itself.
export interface DmSurfaceHealDeps {
  isCurrent(wsId: string): boolean;
  readOut(wsId: string): boolean;
  reachable(peerId: string): boolean;
  reread(wsId: string, peerIds: readonly string[]): Promise<void>;
}

// A badged DM peer the rosters don't hold re-reads them, one read per workspace at a time.
// A read already out may predate the peer, so each settle pumps again and only the peers
// still without a row are re-read.
export class DmSurfaceHealPump {
  constructor(
    private readonly gate: DmSurfaceHealGate,
    private readonly pending: Map<string, Set<string>>,
    private readonly running: Set<string>,
    private readonly deps: DmSurfaceHealDeps,
  ) {}

  heal(wsId: string, peerIds: readonly string[]): void {
    if (!this.deps.isCurrent(wsId) || peerIds.length === 0) return;
    const pending = this.pending.get(wsId) ?? new Set<string>();
    for (const peerId of peerIds) pending.add(peerId);
    this.pending.set(wsId, pending);
    this.pump(wsId);
  }

  pump(wsId: string): void {
    const pending = this.pending.get(wsId);
    if (!pending) return;
    if (!this.deps.isCurrent(wsId)) {
      this.pending.delete(wsId);
      return;
    }
    if (this.running.has(wsId) || this.deps.readOut(wsId)) return;
    this.pending.delete(wsId);
    const now = Date.now();
    const due = [...pending].filter(
      (peerId) => !this.deps.reachable(peerId) && this.gate.shouldHeal(wsId, peerId, now),
    );
    if (due.length === 0) return;
    this.running.add(wsId);
    void this.deps.reread(wsId, due).finally(() => {
      this.running.delete(wsId);
      this.pump(wsId);
    });
  }
}

// Which badged ids in a MERGED per-workspace counts map (channel ids first, then DM
// peers) are genuine surface-less agent DMs — the only set worth a targeted read.
// CONSERVATIVE on purpose: an id this client cannot classify (a channel it was removed
// from, a roster that has not loaded) stays a candidate and costs at most one bounded
// empty read, never a lost door.
export function orphanAgentDmPeerIds(
  counts: Record<string, number>,
  channelIds: readonly string[],
  memberIds: readonly string[],
  cyboIds: readonly string[],
  sessions: readonly AgentDmSessionRow[],
  viewer: ViewerIdentity,
): string[] {
  const channels = new Set(channelIds);
  const members = new Set(memberIds);
  const out: string[] = [];
  for (const [id, n] of Object.entries(counts)) {
    if (n <= 0 || channels.has(id) || members.has(id) || isMe(id, viewer)) continue;
    if (agentDmHasReachableSurface(id, cyboIds, sessions, viewer)) continue;
    out.push(id);
  }
  return out;
}

// WHO the Activity row above is FROM. `fromName` is the daemon's stamp (a cybo's
// name, else its provider label); the fallback used to be the peer id, i.e. a uuid
// as the row's title. Delegates to the ONE display resolver, which already splits
// the states: live session → its real name; LOADED roster without it → "Deleted
// agent"; EMPTY roster → "Agent", claiming no more than "an agent sent this"
// (an empty roster is indistinguishable from not-fetched — isDeletedCyboBinding).
export function agentDmPeerName(
  fromName: string | null | undefined,
  peerId: string,
  sessions: readonly (AgentDmSessionRow & AgentNameSource)[],
  cybos: readonly { id: string; name: string }[],
): string {
  if (fromName && fromName.trim() !== "") return fromName;
  return agentDisplayName(sessions.find((s) => s.agentId === peerId) ?? { cyboId: peerId }, cybos);
}

// The relay's deleted-agent notice: a system DM from the agent's id to the person; it badges nothing,
// so the Activity row is where it surfaces (the relay also writes that row; this is the live twin).
const HELD_PROMPT_NOTICE_PREFIX = "sysn_refused_";

export interface HeldNoticeMessage {
  id: string;
  fromId: string;
  fromType: string;
  toId?: string | null;
  channelId?: string | null;
  parentId?: string | null;
}

// The agent peer a held-prompt notice is from; undefined when `m` is not one addressed to the viewer.
export function heldPromptNoticePeer(
  m: HeldNoticeMessage,
  viewer: ViewerIdentity,
): string | undefined {
  if (m.fromType !== "system" || !m.id.startsWith(HELD_PROMPT_NOTICE_PREFIX)) return undefined;
  if (m.channelId != null || m.parentId || !m.fromId || m.fromId === "system") return undefined;
  return isMe(m.toId ?? "", viewer) ? m.fromId : undefined;
}
